import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import CameraScannerButton from "@/components/CameraScannerButton";
import { beep } from "@/lib/beep";
import { Search, Plus, Minus, Trash2, Loader2, ScanLine, PackageMinus, LogOut, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const MOTIVOS = [
  { v: "VENDA", label: "Venda" },
  { v: "USO_INTERNO", label: "Uso Interno" },
  { v: "PRODUCAO", label: "Produção" },
  { v: "PERDA_DESCARTE", label: "Perda / Descarte" },
  { v: "OUTRO", label: "Outro" },
];
const OBS_OBRIGATORIA = ["PERDA_DESCARTE", "OUTRO"];

export default function RegistrarSaida() {
  const { user } = useAuth();
  const [produtos, setProdutos] = useState([]);
  const [units, setUnits] = useState([]);
  const [unitId, setUnitId] = useState("");
  const [busca, setBusca] = useState("");
  const [scan, setScan] = useState("");
  const [cart, setCart] = useState({}); // sku_id -> {produto, qtd}
  const [fechar, setFechar] = useState(false);
  const [motivo, setMotivo] = useState("VENDA");
  const [obs, setObs] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/produtos").then((r) => setProdutos(r.data.filter((p) => p.ativo !== false)));
    api.get("/units").then((r) => {
      const uu = r.data.filter((u) => u.ativo !== false);
      setUnits(uu);
      if (uu.length) setUnitId(uu[0].id);
    });
  }, []);

  const results = useMemo(() => {
    const b = busca.trim().toLowerCase();
    if (!b) return [];
    return produtos.filter((p) => p.nome.toLowerCase().includes(b) || (p.sku || "").toLowerCase().includes(b) || (p.ean || "").includes(b) || (p.marca || "").toLowerCase().includes(b)).slice(0, 15);
  }, [produtos, busca]);

  const add = (p, q = 1) => setCart((c) => ({ ...c, [p.id]: { produto: p, qtd: (c[p.id]?.qtd || 0) + q } }));
  const addFromSearch = (p) => { add(p, 1); try { beep(); } catch (_) {} setBusca(""); };
  const setQtd = (id, q) => setCart((c) => (q <= 0 ? removeItem(c, id) : { ...c, [id]: { ...c[id], qtd: q } }));
  const removeItem = (c, id) => { const n = { ...c }; delete n[id]; return n; };

  const doScan = (codeArg) => {
    const code = (codeArg ?? scan).trim();
    if (!code) return;
    const p = produtos.find((x) => x.ean === code || x.sku === code || x.ean_master === code);
    if (!p) { toast.error("Produto não encontrado"); return; }
    const q = p.ean_master === code ? (p.fator_conversao || 1) : 1;
    try { beep(); } catch (_) {}
    add(p, q);
    toast.success(`${p.nome} +${q}`);
    setScan("");
  };

  const items = Object.values(cart);
  const totalQtd = items.reduce((s, i) => s + i.qtd, 0);
  const unitNome = units.find((u) => u.id === unitId);
  const obsInvalida = OBS_OBRIGATORIA.includes(motivo) && !obs.trim();

  const finalizar = async () => {
    if (obsInvalida) return toast.error("Observação obrigatória para este motivo");
    setSaving(true);
    try {
      const payload = {
        unit_id: unitId, motivo, observacao: obs,
        itens: items.map((i) => ({ sku_id: i.produto.id, quantidade: i.qtd })),
      };
      const { data } = await api.post("/estoque/saida", payload);
      toast.success(`Saída ${data.numero} registrada — ${data.total_itens} produto(s)`);
      setCart({}); setObs(""); setMotivo("VENDA"); setFechar(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao registrar saída"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="saida-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs text-primary uppercase tracking-[0.18em] font-bold">Baixa de Estoque · PDV</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase flex items-center gap-2"><PackageMinus className="w-7 h-7 text-primary" /> Registrar Saída</h1>
        </div>
        <div className="min-w-[220px]">
          <Label className="text-xs">Unidade / CD</Label>
          <Select value={unitId} onValueChange={setUnitId}>
            <SelectTrigger data-testid="saida-unidade"><SelectValue placeholder="Selecione" /></SelectTrigger>
            <SelectContent>{units.map((u) => <SelectItem key={u.id} value={u.id}>{u.codigo} — {u.nome} ({u.tipo})</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-4 max-w-xl mx-auto lg:max-w-3xl pb-4">
        {/* Escanear / Bipar */}
        <div className="rounded-lg border border-primary/40 bg-black/40 p-4 space-y-3">
          <div className="flex items-center gap-2 text-primary text-xs font-bold uppercase tracking-[0.18em]"><ScanLine className="w-4 h-4" /> Escanear / Bipar</div>
          <div className="flex gap-2">
            <Input autoFocus value={scan} onChange={(e) => setScan(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doScan()}
              placeholder="EAN / SKU / Master" inputMode="text" autoComplete="off" data-testid="saida-scan-input"
              className="text-xl font-mono h-12 bg-black/70 border-2 border-primary/60 text-primary" />
            <Button type="button" variant="outline" className="h-12" onClick={() => doScan()} data-testid="saida-scan-btn"><ScanLine className="w-5 h-5" /></Button>
            <CameraScannerButton continuous onDetected={(c) => doScan(c)} className="h-12 px-4 shrink-0" testid="saida-camera-btn" />
          </div>
          <p className="text-[11px] text-muted-foreground">No celular, use a câmera para bipar vários produtos em sequência sem reabrir.</p>
        </div>

        {/* Buscar manualmente (só mostra resultados ao digitar) */}
        <div className="space-y-1.5">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar produto por nome ou SKU" className="pl-9 h-12" data-testid="saida-busca" />
          </div>
          {busca.trim() && (
            <div className="space-y-1.5 max-h-[300px] overflow-y-auto rounded-md border border-border bg-card/40 p-1.5" data-testid="saida-resultados">
              {results.map((p) => (
                <button key={p.id} onClick={() => addFromSearch(p)} data-testid={`saida-prod-${p.sku}`}
                  className="w-full text-left rounded-md hover:bg-secondary/50 px-3 py-2 flex items-center justify-between">
                  <div><div className="text-sm font-medium">{p.nome}</div><div className="text-[10px] font-mono text-muted-foreground">{p.sku} · {p.marca}</div></div>
                  <Plus className="w-4 h-4 text-primary" />
                </button>
              ))}
              {!results.length && <div className="text-sm text-muted-foreground p-3">Nenhum produto encontrado.</div>}
            </div>
          )}
        </div>

        {/* Itens da Saída */}
        <Card className="bg-card/70 p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="font-display uppercase font-bold">Itens da Saída</div>
            <span className="text-xs font-mono text-muted-foreground" data-testid="saida-total">{items.length} produto(s) · {totalQtd} un</span>
          </div>
          <div className="space-y-2 min-h-[120px]">
            {!items.length && <div className="text-sm text-muted-foreground text-center py-10">Bipe ou busque produtos para dar baixa.</div>}
            {items.map(({ produto: p, qtd }) => (
              <div key={p.id} className="flex items-center gap-2 rounded-md border border-border bg-black/30 px-3 py-2" data-testid={`saida-item-${p.sku}`}>
                <div className="min-w-0 flex-1"><div className="text-sm truncate">{p.nome}</div><div className="text-[10px] font-mono text-muted-foreground">{p.sku}</div></div>
                <div className="flex items-center gap-1">
                  <Button size="icon" variant="outline" className="h-9 w-9" onClick={() => setQtd(p.id, qtd - 1)} data-testid={`saida-minus-${p.sku}`}><Minus className="w-3 h-3" /></Button>
                  <Input type="number" min={1} value={qtd} onChange={(e) => setQtd(p.id, Number(e.target.value) || 0)} className="w-16 h-9 text-center font-mono" data-testid={`saida-qtd-${p.sku}`} />
                  <Button size="icon" variant="outline" className="h-9 w-9" onClick={() => setQtd(p.id, qtd + 1)} data-testid={`saida-plus-${p.sku}`}><Plus className="w-3 h-3" /></Button>
                  <Button size="icon" variant="outline" className="h-9 w-9 text-red-400" onClick={() => setCart((c) => removeItem(c, p.id))} data-testid={`saida-del-${p.sku}`}><Trash2 className="w-3 h-3" /></Button>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Finalizar — logo abaixo dos itens, acompanha o conteúdo */}
        <Button className="w-full h-14 text-lg font-bold shadow-lg mb-[calc(env(safe-area-inset-bottom)+12px)]" disabled={!items.length || !unitId} onClick={() => setFechar(true)} data-testid="btn-finalizar-saida">
          <LogOut className="w-5 h-5" /> Finalizar Saída {items.length ? `· ${totalQtd} un` : ""}
        </Button>
      </div>

      {/* Modal de fechamento */}
      <Dialog open={fechar} onOpenChange={setFechar}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display uppercase">Fechar Saída</DialogTitle>
            <DialogDescription>Confirme o motivo e revise o resumo antes de dar baixa no estoque.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Motivo da saída</Label>
              <Select value={motivo} onValueChange={setMotivo}>
                <SelectTrigger data-testid="saida-motivo"><SelectValue /></SelectTrigger>
                <SelectContent>{MOTIVOS.map((m) => <SelectItem key={m.v} value={m.v}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Observação {OBS_OBRIGATORIA.includes(motivo) ? <span className="text-red-400">(obrigatória)</span> : <span className="text-muted-foreground">(opcional)</span>}</Label>
              <Textarea value={obs} onChange={(e) => setObs(e.target.value)} rows={2} data-testid="saida-obs" />
            </div>
            <div className="rounded-md bg-secondary/40 p-3 text-sm space-y-1" data-testid="saida-resumo">
              <div className="flex justify-between"><span className="text-muted-foreground">Unidade</span><span className="font-medium">{unitNome ? `${unitNome.codigo} — ${unitNome.nome}` : "—"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Responsável</span><span>{user.name}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Produtos / Total</span><span className="font-mono">{items.length} · {totalQtd} un</span></div>
              <div className="border-t border-border mt-1 pt-1 max-h-32 overflow-y-auto">
                {items.map(({ produto: p, qtd }) => <div key={p.id} className="flex justify-between text-xs"><span className="truncate mr-2">{p.nome}</span><span className="font-mono">{qtd}</span></div>)}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFechar(false)}>Cancelar</Button>
            <Button onClick={finalizar} disabled={saving || obsInvalida} data-testid="btn-confirmar-saida">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4" /> Confirmar Baixa</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
